<template>
  <div :class="containerClass">
    <page-header :title="title" :breadcrumb="breadcrumb" :container-class="null" class="mb-32pt" />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <!-- Name -->
      <b-form-group label="Name" label-for="name" label-class="form-label">
        <b-form-input id="name" placeholder="e.g. Credentia CNA" v-model="name" required />
      </b-form-group>

      <b-form-group label="Provider Name" label-for="provider_name" label-class="form-label">
        <b-form-input id="provider_name" placeholder="e.g. Credentia" v-model="provider_name" />
      </b-form-group>

      <b-form-group label="Provider Website" label-for="provider_website" label-class="form-label">
        <b-form-input id="provider_website" placeholder="https://..." v-model="provider_website" />
      </b-form-group>

      <b-form-group label="Program" label-for="program" label-class="form-label">
        <v-select
          id="program"
          class="form-control v-select-custom"
          label="title"
          v-model="program"
          :reduce="(item) => item.id"
          placeholder="Select Program"
          :options="allPrograms"
          :loading="areProgramsLoading"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" v-bind="attributes" v-on="events" />
          </template>
          <template slot="option" slot-scope="option">
            <span>{{ option.title }}</span>
          </template>
          <template slot="selected-option" slot-scope="option">
            {{ option.title }}
          </template>
        </v-select>
      </b-form-group>

      <!-- Coverage -->
      <b-form-group label-class="form-label">
        <b-form-checkbox v-model="is_national" name="national-button" switch>
          National (visible to all schools)
        </b-form-checkbox>
      </b-form-group>

      <b-form-group v-if="!is_national" label="States" label-for="states" label-class="form-label">
        <v-select
          id="states"
          class="form-control v-select-custom"
          label="text"
          v-model="states"
          multiple
          :reduce="(item) => item.value"
          placeholder="Select covered states"
          :options="stateOptions"
        >
        </v-select>
        <small class="form-text text-muted">Only schools in these states will see this provider.</small>
      </b-form-group>

      <!-- What gets tested -->
      <page-separator title="Assessment" />
      <b-form-group label-class="form-label">
        <b-form-checkbox v-model="theory_tested" name="theory-button" switch> Theory tested </b-form-checkbox>
      </b-form-group>
      <b-form-group label-class="form-label">
        <b-form-checkbox v-model="skill_tested" name="skill-button" switch> Skill tested </b-form-checkbox>
      </b-form-group>
      <b-form-group label-class="form-label">
        <b-form-checkbox v-model="clinical_competency_required" name="clinical-button" switch>
          Clinical competency required
        </b-form-checkbox>
      </b-form-group>

      <b-form-group label-class="form-label">
        <b-form-checkbox v-model="is_active" name="active-button" switch> Active? </b-form-checkbox>
      </b-form-group>

      <b-btn variant="primary" :disabled="isLoading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? $t('update') : $t('add') }}</span>
      </b-btn>
    </b-form>
  </div>
</template>

<script>
import { get, map } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import PageHeader from '@/components/Ui/PageHeader.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import Page from '../../../components/Page.vue';
import { USER_ROLES } from '../../../common/constants';

var UsaStates = require('usa-states').UsaStates;

export default {
  name: 'AddTestProvider',
  components: {
    PageHeader,
    PageSeparator,
  },
  extends: Page,

  data() {
    return {
      USER_ROLES,
      title: this.$route.params.id ? 'Edit Test Provider' : 'Add Test Provider',

      name: '',
      provider_name: '',
      provider_website: '',
      program: null,
      is_national: false,
      states: [],
      theory_tested: false,
      skill_tested: false,
      clinical_competency_required: false,
      is_active: true,

      allPrograms: [],
      stateOptions: [],
      areProgramsLoading: false,
      isLoading: false,
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Test Providers', to: { name: 'manage-test-providers' } },
        {
          text: this.$route.params.id ? 'Edit Test Provider' : 'Add Test Provider',
          active: true,
        },
      ];
    },
  },

  methods: {
    ...mapActions('testProvider', ['createTestProvider', 'getTestProvider', 'updateTestProvider']),
    ...mapActions('program', ['getAllPrograms']),
    get,

    fillStateOptions() {
      const usStates = new UsaStates();
      this.stateOptions = map(usStates.states, (state) => ({
        value: state.name,
        text: state.name,
      }));
    },

    async fetchPrograms() {
      this.areProgramsLoading = true;
      try {
        const response = await this.getAllPrograms({ limit: 1000 });
        this.allPrograms = get(response, 'data.results', get(response, 'data', []));
      } catch (e) {
        this.allPrograms = [];
      }
      this.areProgramsLoading = false;
    },

    async onSubmit() {
      this.isLoading = true;
      try {
        const data = {
          name: this.name,
          provider_name: this.provider_name,
          provider_website: this.provider_website,
          program: this.program,
          is_national: this.is_national,
          states: this.is_national ? [] : this.states,
          theory_tested: this.theory_tested,
          skill_tested: this.skill_tested,
          clinical_competency_required: this.clinical_competency_required,
          is_active: this.is_active,
        };

        if (this.$route.params.id) {
          await this.updateTestProvider({ id: this.$route.params.id, data });
          this.makeToast({ variant: 'success', msg: 'Test Provider Updated Successfully!' });
        } else {
          await this.createTestProvider(data);
          this.makeToast({ variant: 'success', msg: 'Test Provider Added Successfully!' });
        }
        setTimeout(() => this.$router.push({ name: 'manage-test-providers' }), 250);
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },
  },

  async mounted() {
    this.fillStateOptions();
    this.fetchPrograms();

    if (this.$route.params.id) {
      this.isLoading = true;
      try {
        const resp = (await this.getTestProvider(this.$route.params.id)).data;
        this.name = resp.name;
        this.provider_name = resp.provider_name;
        this.provider_website = resp.provider_website;
        this.program = get(resp, 'program.id', resp.program);
        this.is_national = resp.is_national;
        this.states = resp.states || [];
        this.theory_tested = resp.theory_tested;
        this.skill_tested = resp.skill_tested;
        this.clinical_competency_required = resp.clinical_competency_required;
        this.is_active = resp.is_active;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        this.$router.push({ name: 'manage-test-providers' });
      }
      this.isLoading = false;
    }
  },
};
</script>
